import React, { useState, useEffect } from "react";
import axios from "axios";
import dayjs from "dayjs";
import {
  Button,
  Modal,
  Spin,
  Alert,
  Tag,
  Card,
  Typography,
  Table,
  Menu,
  Dropdown,
  Row,
  Col,
} from "antd";
import API_BASE_URL from "../../config/api";
import { ELEVENLABS_CONFIG } from "../../config/elevenlabs-api";
import { BatchCallForm } from "./batchCallForm";
import * as XLSX from "xlsx";
import { DownloadOutlined, EyeOutlined, PlusOutlined } from "@ant-design/icons";
import CustomPagination from "../paginations/CustomPagination";
// import { Dropdown } from "bootstrap/dist/js/bootstrap.bundle.min";

const { Title, Text } = Typography;

const BatchCallList = () => {
  const [batchCalls, setBatchCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingBatchCall, setEditingBatchCall] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [selectedBatchCall, setSelectedBatchCall] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [batchCallDetail, setBatchCallDetail] = useState(null);
  const [dispositions, setDispositions] = useState({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  useEffect(() => {
    fetchBatchCalls();
  }, []);

  const exportRecipientsData = (format) => {
    if (
      !batchCallDetail?.recipients ||
      batchCallDetail.recipients.length === 0
    ) {
      return;
    }
    const data = batchCallDetail.recipients.map((recipient) => ({
      "Phone Number": recipient.phone_number || "",
      "User Name":
        recipient.conversation_initiation_client_data?.dynamic_variables
          ?.call_user_name || "N/A",
      Status: recipient.status || "",
      "Disposition Status":
        dispositions[recipient.conversation_id]?.status || "NO DISPOSITION",
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Recipients");

    if (format === "csv") {
      XLSX.writeFile(
        workbook,
        `${batchCallDetail.name || "campaign"}_recipients.csv`,
        { bookType: "csv" }
      );
    } else if (format === "xlsx") {
      XLSX.writeFile(
        workbook,
        `${batchCallDetail.name || "campaign"}_recipients.xlsx`,
        { bookType: "xlsx" }
      );
    }
  };

  const fetchBatchCalls = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/batch-calls`, {
        headers: { Authorization: token },
      });
      setBatchCalls(response.data);
      setPagination((prev) => ({
        ...prev,
        total: response.data.length,
      }));
    } catch (err) {
      setError("Failed to fetch batch calls");
    } finally {
      setLoading(false);
    }
  };

  const fetchBatchCallDetail = async (batchCallId) => {
    setDetailLoading(true);
    setDetailError("");
    try {
      const response = await axios.get(
        `${ELEVENLABS_CONFIG.BASE_URL}/batch-calling/${batchCallId}`,
        { headers: { "xi-api-key": ELEVENLABS_CONFIG.API_KEY } }
      );

      const batchDetail = response.data;

      const token = localStorage.getItem("token");
      const dispositionsRes = await axios.get(`${API_BASE_URL}/dispositions`, {
        headers: { Authorization: token },
      });

      const dispositionMap = {};
      dispositionsRes.data.data.dispositions.forEach((d) => {
        dispositionMap[d.conversation_id] = d.status;
      });

      setDispositions(dispositionMap);
      setBatchCallDetail(batchDetail);
    } catch (err) {
      console.error(err);
      setDetailError("Failed to fetch batch call details");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleViewDetail = (batchCall) => {
    setSelectedBatchCall(batchCall);
    fetchBatchCallDetail(batchCall.id);
    setShowDetail(true);
  };

  const handleDetailClose = () => {
    setSelectedBatchCall(null);
    setBatchCallDetail(null);
    setShowDetail(false);
  };

  const handleEditBatchCall = (batchCall) => {
    setEditingBatchCall(batchCall);
    setShowForm(true);
  };

  const handleDeleteBatchCall = async (id) => {
    Modal.confirm({
      title: "Are you sure you want to delete this batch call group?",
      onOk: async () => {
        try {
          const token = localStorage.getItem("token");
          await axios.delete(`${API_BASE_URL}/batch-calls/${id}`, {
            headers: { Authorization: token },
          });
          fetchBatchCalls();
        } catch (err) {
          setError("Failed to delete batch call group");
        }
      },
    });
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingBatchCall(null);
  };

  const handleFormSubmit = () => {
    setShowForm(false);
    setEditingBatchCall(null);
    fetchBatchCalls();
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, current: page }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination({
      current: 1,
      pageSize: newPageSize,
      total: pagination.total,
    });
  };

  return (
    <div className="p-4">
      <Row
        justify="space-between"
        align="middle"
        gutter={[16, 16]}
        style={{ marginBottom: "20px" }}
      >
        <Col xs={24} sm={24} md={12} lg={12}>
          <Title level={2}>Campaigning Management</Title>
        </Col>
        <Col xs={24} sm={24} md={12} lg={12}>
          <Row gutter={[16, 16]} justify="end">
            <Col xs={24} sm={12} md={12} lg={12}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditingBatchCall(null);
                  setShowForm(true);
                }}
                style={{ width: "100%" }}
              >
                Add Campaign Group
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
      {error && (
        <Alert message={error} type="error" showIcon className="mb-4" />
      )}
      {loading ? (
        <Spin size="large" className="d-flex justify-content-center" />
      ) : batchCalls.length === 0 ? (
        <Alert
          message="No batch call groups found. Create your first batch call group!"
          type="info"
          showIcon
        />
      ) : (
        <>
          <div
            className="rounded-2 table-responsive"
            style={{ overflowX: "auto" }}
          >
            <table
              className="table align-middle mb-0 shadow-sm"
              style={{ tableLayout: "auto", minWidth: "600px" }}
            >
              <thead className="table-light">
                <tr>
                  <th>Campaign Name</th>
                  <th className="text-center">Agent</th>
                  <th className="text-center">Number of Counts</th>
                  <th>Created Date</th>
                  <th>Scheduled Time</th>
                  <th className="text-center" style={{ width: "100px" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {batchCalls
                  .slice(
                    (pagination.current - 1) * pagination.pageSize,
                    pagination.current * pagination.pageSize
                  )
                  .map((batchCall) => (
                    <tr key={batchCall.id}>
                      <td>{batchCall.name}</td>
                      <td className="text-center">
                        {batchCall.agent_id || "N/A"}
                      </td>
                      <td className="text-center">
                        {batchCall.total_calls_scheduled || 0}
                      </td>
                      <td>
                        {batchCall.created_at
                          ? dayjs(batchCall.created_at).format("DD-MM-YY")
                          : "N/A"}
                      </td>
                      <td>
                        {dayjs(batchCall.scheduled_time).format("DD-MM-YY")}
                      </td>
                      <td
                        style={{ whiteSpace: "nowrap" }}
                        className="text-center"
                      >
                        <Button
                          type="text"
                          icon={<EyeOutlined style={{ color: "blue" }} />}
                          onClick={() => handleViewDetail(batchCall)}
                          size="small"
                        />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <div className="d-flex justify-content-end">
            <CustomPagination
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={pagination.total}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          </div>
        </>
      )}

      <Modal
        title={editingBatchCall ? "Edit Campaign" : "Create Campaign"}
        open={showForm}
        onCancel={handleFormClose}
        footer={null}
        width={800}
        destroyOnClose={true}
      >
        <BatchCallForm
          batchCall={editingBatchCall}
          onClose={handleFormClose}
          onSubmit={handleFormSubmit}
        />
      </Modal>

      <Modal
        title={`Campaign Details - ${
          batchCallDetail?.name || selectedBatchCall?.name || ""
        }`}
        open={showDetail}
        onCancel={handleDetailClose}
        footer={[
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item
                  key="csv"
                  onClick={() => exportRecipientsData("csv")}
                >
                  Export as CSV
                </Menu.Item>
                <Menu.Item
                  key="xlsx"
                  onClick={() => exportRecipientsData("xlsx")}
                >
                  Export as Excel
                </Menu.Item>
              </Menu>
            }
            trigger={["click"]}
          >
            <Button icon={<DownloadOutlined />} style={{ marginRight: 8 }}>
              Export
            </Button>
          </Dropdown>,
          <Button key="close" onClick={handleDetailClose}>
            Close
          </Button>,
        ]}
        width={800}
        destroyOnClose={true}
      >
        {detailLoading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Spin size="large" />
          </div>
        ) : detailError ? (
          <Alert message={detailError} type="error" showIcon />
        ) : (
          <div style={{ padding: "10px 0" }}>
            <div style={{ marginBottom: "16px" }}>
              <Text strong>Total recipients: </Text>
              <Text>
                {batchCallDetail?.recipients?.length ||
                  selectedBatchCall?.contact_ids?.length ||
                  0}
              </Text>
            </div>

            <div style={{ marginBottom: "16px" }}>
              <Text strong>Scheduled Time: </Text>
              <Text>
                {batchCallDetail
                  ? dayjs(batchCallDetail.scheduled_time_unix * 1000).format(
                      "DD-MM-YY hh:mm:ss A"
                    )
                  : selectedBatchCall?.scheduled_time
                  ? dayjs(selectedBatchCall.scheduled_time).format(
                      "DD-MM-YY hh:mm:ss A"
                    )
                  : "N/A"}
              </Text>
            </div>

            <div style={{ marginBottom: "16px" }}>
              <Text strong>Progress: </Text>
              <Text>
                {batchCallDetail?.progress ||
                  selectedBatchCall?.progress ||
                  "N/A"}
              </Text>
            </div>

            <Title
              level={4}
              style={{ marginTop: "24px", marginBottom: "16px" }}
            >
              Call Recipients
            </Title>
            <Table
              className="hidden-scroll"
              dataSource={batchCallDetail?.recipients || []}
              columns={[
                {
                  title: "Phone Number",
                  dataIndex: "phone_number",
                  key: "phone_number",
                  render: (phone) => phone || "N/A",
                },
                {
                  title: "User Name",
                  key: "user_name",
                  render: (_, record) =>
                    record.conversation_initiation_client_data
                      ?.dynamic_variables?.call_user_name || "N/A",
                },
                {
                  title: "Status",
                  dataIndex: "status",
                  key: "status",
                  render: (status) => (
                    <Tag
                      color={
                        status === "completed"
                          ? "green"
                          : status === "initiated"
                          ? "blue"
                          : status === "failed"
                          ? "red"
                          : "default"
                      }
                    >
                      {status ? status.toUpperCase() : "N/A"}
                    </Tag>
                  ),
                },
                {
                  title: "Disposition Status",
                  key: "disposition_status",
                  render: (_, record) => {
                    const status = dispositions[record.conversation_id];
                    return (
                      <Tag
                        color={
                          status
                            ? status.toLowerCase() === "interested"
                              ? "green"
                              : status.toLowerCase() === "not interested"
                              ? "red"
                              : "yellow"
                            : undefined
                        }
                      >
                        {status ? status.toUpperCase() : "NO DISPOSITION"}
                      </Tag>
                    );
                  },
                },
              ]}
              pagination={false}
              rowKey="phone_number"
              size="small"
              scroll={{ y: 300 }}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default BatchCallList;
